import cn from "classnames"
import { Fragment } from "react"

import { Flex, Link, Text, Tooltip } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { FindingStatusTag } from "@/molecules/FindingStatusTag"
import { Item, List } from "@/pages/DiscoveryPage/components/molecules/List"
import { IntroducedThroughFixesItem } from "./IntroducedThroughFixesItem"
import { IntroducedTroughItem } from "./IntroducedTroughItem"
import { RemediationItem } from "./RemediationItem"

import styles from "./SourceList.module.scss"

import type { SourceListComponent } from "."

export const SourceList: SourceListComponent = (props) => {
  const { t } = useTranslation()
  const { sources, type, noLimits } = props
  const tKey = "inventory.images.drawer.issue."
  const isRemediation = type === "remedies"
  const hasMultipleSources = sources.length > 1
  const Component = hasMultipleSources ? "li" : "div"

  return (
    <List
      items={sources}
      limit={noLimits ? undefined : 2}
      Component="ol"
      classes={
        hasMultipleSources
          ? { list: styles.list }
          : { list: cn(styles.list, styles.listWithoutPadding) }
      }
      showMoreTitle={
        isRemediation ? undefined : `${t(`${tKey}showMoreSources`)}`
      }
      showLessTitle={
        isRemediation ? undefined : `${t(`${tKey}showLessSources`)}`
      }
      render={(
        {
          name,
          introducedThrough,
          remedies,
          computationError,
          packageName,
          fixVersions,
          sourceControlLink,
          resolutionData,
          isResolved
        },
        index
      ) => {
        const hasFixVersion = fixVersions && fixVersions?.length > 0
        const hasCodeRepoFixes = remedies.length > 0
        const hasIntroducedThrough = introducedThrough.length > 0
        const hasDirectPackage =
          hasIntroducedThrough &&
          introducedThrough.some(
            (packageRecord) => packageRecord?.[0]?.name === packageName
          )
        const hasIndirectPackage = !hasDirectPackage && hasIntroducedThrough
        const showIndirectSuggestion =
          !hasCodeRepoFixes && hasIndirectPackage && hasFixVersion
        const showLinkToSourceControl = isRemediation && sourceControlLink
        const sourceNameComponent = (
          <Text ellipsis underline={isRemediation}>
            {name}
          </Text>
        )
        const isIntroducedThrough = type === "introducedThrough"
        // const Wrapper = !!name && name !== "." ? Tooltip : Fragment

        return (
          <Fragment key={index}>
            <Item
              title={
                <Flex gap={8} align="center">
                  <div className={styles.titleItemContainer}>
                    {`Source: `}
                    <Tooltip title={name}>
                      {showLinkToSourceControl ? (
                        <Link
                          key={name}
                          href={sourceControlLink}
                          target="_blank"
                          type="link"
                          className={styles.ellipsis}
                        >
                          {sourceNameComponent}
                        </Link>
                      ) : (
                        sourceNameComponent
                      )}
                    </Tooltip>
                  </div>
                  {isIntroducedThrough && isResolved && (
                    <FindingStatusTag
                      type="CodeRepository"
                      text={t("issue.resolved.findings.resolvedInSource")}
                      resolutionData={resolutionData || undefined}
                    />
                  )}
                </Flex>
              }
              Component={Component}
              classes={{ title: styles.titleItem }}
            />

            {isIntroducedThrough && (
              <IntroducedTroughItem introducedThrough={introducedThrough} />
            )}
            {isRemediation && (
              <>
                {hasCodeRepoFixes && (
                  <RemediationItem
                    remedies={remedies}
                    introducedThrough={introducedThrough}
                    noLimits={noLimits}
                  />
                )}
                {!hasCodeRepoFixes &&
                  !computationError &&
                  (hasDirectPackage || !hasIntroducedThrough) && (
                    <Text className={styles.value}>
                      {`${t(`${tKey}kodemNoFix`)}`}
                    </Text>
                  )}
                {!computationError && showIndirectSuggestion && (
                  <>
                    <Text
                      className={cn(styles.value, styles.noRemediationIndirect)}
                    >
                      {`${t(`${tKey}kodemNoFixIndirect`)}`}
                    </Text>
                    <IntroducedThroughFixesItem
                      introducedThrough={introducedThrough}
                      packageName={packageName}
                      fixVersion={fixVersions[0]}
                      noLimits={noLimits}
                      tKey={tKey}
                    />
                  </>
                )}
                {computationError && (
                  <Text className={styles.errorText}>
                    {`${t(`${tKey}${computationError}`)}`}
                  </Text>
                )}
              </>
            )}
          </Fragment>
        )
      }}
    />
  )
}
